/**
 * PatientCarePlanTab — the patient's care plan as ONE scrolling page with its Progress view (patient pages
 * switch, `?x=patient-pages`; prototype commits 9eb33aa, 22ad00f, 24a9409). One goal card per goal, the
 * cards filling the page width, each with the goal, its target and its latest result, and the status.
 *
 * Source: the C5 care-plan read the encounter's readout already uses (`useCarePlans`,
 * GET /api/Patients/{id}/care-plans). The server's verdict on a goal is carried, never recomputed here
 * (`care-plan-view.ts`). The four outcomes stay apart: still reading, the read failed (never "no plan"),
 * no plan on file, the plans.
 *
 * Left out, no production source: the 12-week board of scheduled orders (the wire has goals, no schedule),
 * the care-team strip (a contributor is a practitioner id and a role, no name), trend curves (needs the lab
 * observation series per goal), adherence bars (no adherence data).
 */
import { Skeleton, StatusChip } from '@scripsteam/scrips-signal-ds';
import { useCarePlans } from '@/encounter/services/use-care-plans';
import { latestLine, planLine, tally, targetLine } from '@/encounter/care-plan-readout';
import type { GoalStatus, GoalView } from '@/encounter/lib/care-plan-view';
import type { Patient } from '@/types/patient-api';

// The same verdict words and tones as the encounter's readout.
const VERDICT: Record<GoalStatus, { semantic: 'success' | 'warning' | 'neutral'; label: string }> = {
  met: { semantic: 'success', label: 'Met' },
  'not-met': { semantic: 'warning', label: 'Not met' },
  'no-data': { semantic: 'neutral', label: 'No result yet' },
  unknown: { semantic: 'neutral', label: 'Status not recognised' },
};

export function PatientCarePlanTab({ patient }: { patient: Patient | null }) {
  const { data: plans, isLoading, isError } = useCarePlans(patient?.patientId ?? null);

  if (!patient?.patientId || isLoading) {
    return (
      <div data-slot="care-plan-page" data-state="loading">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div data-slot="care-plan-page" data-state="error">
        <p data-slot="overview-empty">Could not load the care plan. This is not an empty plan: try again later.</p>
      </div>
    );
  }
  if (!plans || plans.length === 0) {
    return (
      <div data-slot="care-plan-page" data-state="empty">
        <p data-slot="overview-empty">No care plan on file.</p>
      </div>
    );
  }
  return (
    <div data-slot="care-plan-page">
      {plans.map((plan) => (
        <section key={plan.carePlanId} data-slot="care-plan" aria-label={plan.title}>
          <header data-slot="care-plan-head">
            <h2 data-slot="care-plan-title">{planLine(plan)}</h2>
            <span data-slot="care-plan-tally">{tally(plan)}</span>
          </header>
          <h3 data-slot="care-plan-subtitle">Progress</h3>
          {plan.goals.length === 0 ? (
            <p data-slot="overview-empty">This plan has no goals.</p>
          ) : (
            <ul data-slot="care-plan-goals" aria-label={`Goals of ${plan.title}`}>
              {plan.goals.map((goal) => (
                <GoalCard key={goal.id} goal={goal} />
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

function GoalCard({ goal }: { goal: GoalView }) {
  const verdict = VERDICT[goal.status];
  return (
    <li data-slot="goal-card" data-status={goal.status}>
      <div data-slot="goal-card-head">
        <span data-slot="goal-card-title">{goal.title}</span>
        <StatusChip semantic={verdict.semantic} tone="soft">
          {verdict.label}
        </StatusChip>
      </div>
      <span data-slot="goal-card-latest">{latestLine(goal)}</span>
      <span data-slot="goal-card-target">{targetLine(goal)}</span>
    </li>
  );
}
